<template>
  <div class="min-h-screen bg-zinc-950 p-8 text-zinc-100">
    <div class="max-w-4xl mx-auto space-y-8">
      <header class="flex justify-between items-center bg-zinc-900 p-6 rounded-2xl shadow-sm border border-zinc-800">
        <div class="flex items-center gap-4">
          <router-link to="/" class="text-zinc-500 hover:text-green-500 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
          </router-link>
          <h1 class="text-3xl font-bold">Festivals & Événements</h1>
        </div>
        <button @click="createEvent" class="bg-green-500 text-zinc-950 px-4 py-2 rounded-xl text-sm font-bold hover:bg-green-400 transition-colors">
          + Nouvel Événement
        </button>
      </header>

      <main class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Event List -->
        <div class="bg-zinc-900 rounded-2xl shadow-sm border border-zinc-800 overflow-hidden">
          <div class="p-4 border-b border-zinc-800 bg-zinc-800/50">
            <h2 class="font-bold text-zinc-300">Vos événements</h2>
          </div>
          <div class="divide-y divide-zinc-800">
            <div v-if="events.length === 0" class="p-6 text-center text-zinc-500 text-sm">
              Aucun événement.
            </div>
            <button 
              v-for="event in events" :key="event.id"
              @click="selectEvent(event)"
              :class="['w-full text-left p-4 hover:bg-zinc-800 transition-colors', selectedEvent?.id === event.id ? 'bg-green-500/10 border-l-4 border-green-500' : 'border-l-4 border-transparent']"
            >
              <div class="font-medium truncate">{{ event.name }}</div>
              <div class="text-xs text-zinc-500 mt-1">{{ event.date || 'Date non définie' }} • {{ event.spotifyArtistIds.length }} artistes</div>
            </button>
          </div>
        </div>

        <!-- Event Details & Search -->
        <div v-if="selectedEvent" class="lg:col-span-2 space-y-6">
          <div class="bg-zinc-900 p-6 rounded-2xl shadow-sm border border-zinc-800 space-y-6">
            <div class="flex justify-between items-start">
              <div>
                <input v-model="selectedEvent.name" @change="saveEvent" class="text-2xl font-bold bg-transparent border-none p-0 focus:ring-0 w-full text-white" placeholder="Nom de l'événement" />
                <input type="date" v-model="selectedEvent.date" @change="saveEvent" class="text-sm text-zinc-400 bg-transparent border-none p-0 mt-1 focus:ring-0" />
              </div>
              <button @click="deleteEvent(selectedEvent)" class="text-red-500 hover:bg-red-900/30 p-2 rounded-lg transition-colors">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
              </button>
            </div>

            <div class="relative">
              <ComboboxRoot v-model="searchQuery" @update:modelValue="addArtist">
                <div class="relative w-full">
                  <ComboboxInput
                    @input="onSearchInput"
                    placeholder="Rechercher un artiste sur Spotify..."
                    class="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-10 pr-4 py-3 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-shadow text-white placeholder-zinc-500"
                  />
                  <svg class="absolute left-3 top-3.5 w-5 h-5 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                </div>

                <ComboboxContent v-if="searchResults.length > 0" class="absolute z-10 w-full mt-2 bg-zinc-800 rounded-xl shadow-lg border border-zinc-700 max-h-60 overflow-auto">
                  <ComboboxItem
                    v-for="artist in searchResults"
                    :key="artist.id"
                    :value="artist"
                    class="px-4 py-3 hover:bg-green-500/20 cursor-pointer flex items-center gap-3 ui-active:bg-green-500/20 ui-active:text-white text-zinc-300"
                  >
                    <img v-if="artist.images?.length" :src="artist.images[artist.images.length - 1].url" class="w-8 h-8 rounded-full object-cover" />
                    <div v-else class="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-xs text-zinc-500">?</div>
                    <span class="font-medium text-sm">{{ artist.name }}</span>
                  </ComboboxItem>
                </ComboboxContent>
              </ComboboxRoot>
            </div>
            
            <div class="space-y-3">
              <h3 class="font-bold text-zinc-300">Line-up ({{ selectedEvent.spotifyArtistIds.length }})</h3>
              <div v-if="selectedEvent.spotifyArtistIds.length === 0" class="text-sm text-zinc-500 py-4 text-center border-2 border-dashed border-zinc-800 rounded-xl">
                Cherchez et ajoutez des artistes pour cet événement.
              </div>
              <div v-else class="space-y-2">
                <div v-for="artistId in selectedEvent.spotifyArtistIds" :key="artistId" class="flex items-center justify-between p-3 bg-zinc-950 rounded-xl border border-zinc-800 group">
                  <div class="flex items-center gap-3">
                    <span class="font-medium">{{ artistMap[artistId]?.name || artistId }}</span>
                    <span v-if="stats[artistId] > 0" class="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-bold">
                      {{ stats[artistId] }} titres likés
                    </span>
                    <span v-if="ratings[artistId]?.isDisliked" class="text-xs bg-red-900/30 text-red-400 px-2 py-0.5 rounded-full font-bold">
                      Masqué (Disliké)
                    </span>
                  </div>
                  <div class="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button @click="toggleDislike(artistId)" class="p-1.5 text-zinc-500 hover:text-red-400 rounded bg-zinc-900 shadow-sm" title="Disliker / Masquer">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clip-rule="evenodd"></path></svg>
                    </button>
                    <button @click="removeArtist(artistId)" class="p-1.5 text-zinc-500 hover:text-red-400 rounded bg-zinc-900 shadow-sm" title="Retirer de l'événement">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="bg-gradient-to-r from-zinc-800 to-zinc-900 p-6 rounded-2xl shadow-md text-white flex justify-between items-center border border-zinc-800">
            <div>
              <h3 class="font-bold text-lg">Générer la Playlist</h3>
              <p class="text-zinc-400 text-sm">Crée une playlist sur votre compte Spotify avec vos titres likés de ce line-up.</p>
            </div>
            <button class="bg-green-500 hover:bg-green-400 text-zinc-950 px-6 py-3 rounded-full font-bold transition-all transform hover:scale-105">
              Générer
            </button>
          </div>
        </div>
        
        <div v-else class="lg:col-span-2 bg-zinc-900/50 border-2 border-dashed border-zinc-800 rounded-2xl flex items-center justify-center text-zinc-500 p-12">
          Sélectionnez ou créez un événement pour commencer.
        </div>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { ComboboxRoot, ComboboxInput, ComboboxContent, ComboboxItem } from 'radix-vue'
import { useDebounceFn } from '@vueuse/core'

const events = ref<any[]>([])
const selectedEvent = ref<any>(null)
const searchQuery = ref('')
const searchResults = ref<any[]>([])
const artistMap = ref<Record<string, any>>({}) // cache of artists info
const stats = ref<Record<string, number>>({})
const ratings = ref<Record<string, any>>({})

const api = async (url: string, options: any = {}) => {
  const token = localStorage.getItem('apiToken')
  const res = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    }
  })
  if (!res.ok) throw new Error('API Error')
  return res.json()
}

const loadEvents = async () => {
  events.value = await api('/api/music/events')
}

const createEvent = async () => {
  const newEvent = await api('/api/music/events', {
    method: 'POST',
    body: JSON.stringify({ name: 'Nouvel événement', date: new Date().toISOString().split('T')[0] })
  })
  events.value.unshift(newEvent)
  selectEvent(newEvent)
}

const saveEvent = async () => {
  if (!selectedEvent.value) return
  await api(`/api/music/events/${selectedEvent.value.id}`, {
    method: 'PUT',
    body: JSON.stringify(selectedEvent.value)
  })
  const idx = events.value.findIndex(e => e.id === selectedEvent.value.id)
  if (idx !== -1) events.value[idx] = selectedEvent.value
}

const deleteEvent = async (event: any) => {
  if (!confirm('Supprimer cet événement ?')) return
  await api(`/api/music/events/${event.id}`, { method: 'DELETE' })
  events.value = events.value.filter(e => e.id !== event.id)
  if (selectedEvent.value?.id === event.id) selectedEvent.value = null
}

const selectEvent = async (event: any) => {
  selectedEvent.value = { ...event }
  searchResults.value = []
  searchQuery.value = ''
  await loadStatsAndRatings()
}

const searchCache = new Map<string, any[]>()

const performSearch = useDebounceFn(async (query: string) => {
  if (!query.trim()) {
    searchResults.value = []
    return
  }
  
  if (searchCache.has(query)) {
    searchResults.value = searchCache.get(query)!
    return
  }

  try {
    const data = await api(`/api/music/search?q=${encodeURIComponent(query)}`)
    searchResults.value = data
    searchCache.set(query, data)
    
    // update artist map
    data.forEach((a: any) => {
      artistMap.value[a.id] = a
    })
  } catch (e) {
    console.error(e)
  }
}, 500)

const onSearchInput = (e: Event) => {
  const target = e.target as HTMLInputElement
  performSearch(target.value)
}

const addArtist = async (artist: any) => {
  if (!artist || !selectedEvent.value) return
  
  if (!selectedEvent.value.spotifyArtistIds.includes(artist.id)) {
    selectedEvent.value.spotifyArtistIds.push(artist.id)
    artistMap.value[artist.id] = artist
    await saveEvent()
    await loadStatsAndRatings()
  }
  
  searchQuery.value = ''
  searchResults.value = []
}

const removeArtist = async (artistId: string) => {
  if (!selectedEvent.value) return
  selectedEvent.value.spotifyArtistIds = selectedEvent.value.spotifyArtistIds.filter((id: string) => id !== artistId)
  await saveEvent()
}

const toggleDislike = async (artistId: string) => {
  const current = ratings.value[artistId]?.isDisliked || false
  const updated = await api(`/api/music/ratings/${artistId}`, {
    method: 'PUT',
    body: JSON.stringify({ isDisliked: !current })
  })
  ratings.value[artistId] = updated
}

const loadStatsAndRatings = async () => {
  if (!selectedEvent.value?.spotifyArtistIds.length) return
  
  const [statsData, ratingsData] = await Promise.all([
    api('/api/music/stats', {
      method: 'POST',
      body: JSON.stringify({ artistIds: selectedEvent.value.spotifyArtistIds })
    }),
    api('/api/music/ratings') // For now, fetches all user ratings. Could be optimized.
  ])
  
  stats.value = statsData
  ratings.value = ratingsData
}

onMounted(() => {
  loadEvents()
})
</script>
